import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ChatMessage } from "./chatStore";
import { LS_CASE_BRIEF_CHAT_STORAGE } from "../shared/local-storage/keys";

export type ChatStep = "language" | "decision" | "files" | "dimensions" | "setupComplete" | "chat";

// The guided setup question the user moved on from when jumping to Case Frame, so Back can
// resume it. Persisted so it survives a reload (an in-memory ref would be lost).
export type PausedSetupQuestion = { step: ChatStep; dimensionIndex?: number };

export type UploadedFile = {
  fileId?: string;
  fileName: string;
  documentId: number;
  fileSize: number;
  webUrl?: string;
  // Sherpa has read this document into the brief; saved with the draft so it is never read twice.
  read?: boolean;
};

type ApiChatMessage = {
  role: "user" | "assistant";
  content: string;
};

interface CaseBriefChatState {
  caseBriefId: number | null;
  messages: ChatMessage[];
  step: ChatStep;
  language: string | null;
  vectorStoreId: string | null;
  uploadedFiles: UploadedFile[];
  chatHistory: ApiChatMessage[];
  projectName: string | null;
  projectDescription: string | null;
  iconShape: string | null;
  iconColor: string | null;
  logoUrl: string | null;
  dimensionValues: Record<string, string>;
  pendingDimensionIndex: number;
  deferredDecisionReply: ChatMessage | null;
  pausedSetupQuestion: PausedSetupQuestion | null;

  setCaseBriefId: (id: number | null) => void;
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  setStep: (step: ChatStep) => void;
  setLanguage: (language: string | null) => void;
  setVectorStoreId: (vectorStoreId: string | null) => void;
  setUploadedFiles: (files: UploadedFile[] | ((prev: UploadedFile[]) => UploadedFile[])) => void;
  setChatHistory: (history: ApiChatMessage[]) => void;
  markDocumentsRead: (documents: UploadedFile[]) => void;
  setProjectName: (name: string | null) => void;
  setProjectDescription: (description: string | null) => void;
  setIcon: (shape: string | null, color: string | null) => void;
  setLogoUrl: (logoUrl: string | null) => void;
  setDimensionValues: (values: Record<string, string>) => void;
  patchDimensionValue: (dimensionId: number, value: string) => void;
  setPendingDimensionIndex: (index: number) => void;
  setDeferredDecisionReply: (message: ChatMessage | null) => void;
  setPausedSetupQuestion: (question: PausedSetupQuestion | null) => void;
  reset: () => void;
}

const initialState = {
  caseBriefId: null as number | null,
  messages: [] as ChatMessage[],
  step: "language" as ChatStep,
  language: null as string | null,
  vectorStoreId: null as string | null,
  uploadedFiles: [] as UploadedFile[],
  chatHistory: [] as ApiChatMessage[],
  projectName: null as string | null,
  projectDescription: null as string | null,
  iconShape: null as string | null,
  iconColor: null as string | null,
  logoUrl: null as string | null,
  dimensionValues: {} as Record<string, string>,
  pendingDimensionIndex: 0,
  deferredDecisionReply: null as ChatMessage | null,
  pausedSetupQuestion: null as PausedSetupQuestion | null,
};

export const useCaseBriefChatStore = create<CaseBriefChatState>()(
  persist(
    (set) => ({
      ...initialState,

      setCaseBriefId: (caseBriefId) => set({ caseBriefId }),
      setMessages: (messagesOrFn) =>
        set((state) => ({
          messages: typeof messagesOrFn === "function" ? messagesOrFn(state.messages) : messagesOrFn,
        })),
      setStep: (step) => set({ step }),
      setLanguage: (language) => set({ language }),
      setVectorStoreId: (vectorStoreId) => set({ vectorStoreId }),
      setUploadedFiles: (filesOrFn) =>
        set((state) => ({
          uploadedFiles: typeof filesOrFn === "function" ? filesOrFn(state.uploadedFiles) : filesOrFn,
        })),
      setChatHistory: (chatHistory) => set({ chatHistory }),
      // A document added outside the chat (the Vault panel, an attachment) gets its entry here.
      markDocumentsRead: (documents) =>
        set((state) => {
          const ids = new Set(documents.map((d) => d.documentId));
          const known = new Set(state.uploadedFiles.map((f) => f.documentId));
          return {
            uploadedFiles: [
              ...state.uploadedFiles.map((f) => (ids.has(f.documentId) ? { ...f, read: true } : f)),
              ...documents.filter((d) => !known.has(d.documentId)).map((d) => ({ ...d, read: true }))
            ]
          };
        }),
      setProjectName: (projectName) => set({ projectName }),
      setProjectDescription: (projectDescription) => set({ projectDescription }),
      setIcon: (iconShape, iconColor) => set({ iconShape, iconColor }),
      setLogoUrl: (logoUrl) => set({ logoUrl }),
      setDimensionValues: (dimensionValues) => set({ dimensionValues }),
      patchDimensionValue: (dimensionId, value) =>
        set((state) => {
          const key = String(dimensionId);
          const next = { ...state.dimensionValues };
          if (value.trim() === "") delete next[key];
          else next[key] = value;
          return { dimensionValues: next };
        }),
      setPendingDimensionIndex: (pendingDimensionIndex) => set({ pendingDimensionIndex }),
      setDeferredDecisionReply: (deferredDecisionReply) => set({ deferredDecisionReply }),
      setPausedSetupQuestion: (pausedSetupQuestion) => set({ pausedSetupQuestion }),
      reset: () => set(initialState),
    }),
    {
      name: LS_CASE_BRIEF_CHAT_STORAGE,
      skipHydration: true,
      // A picked photo is a data URL of up to 5 MB, too large for localStorage.
      partialize: (state) => ({ ...state, logoUrl: null }),
    }
  )
);
